import { ChartConfig, ChartContainer, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/chart";
import { EvidenceGateWeeklyPoint } from "@/http/api-types";
import { cn } from "@/lib/utils";
import * as React from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, XAxis, YAxis } from "recharts";
import { buildChartRows, rawAverageKey, stackTotal } from "./check-2";

export const AVERAGE_OPTIONS = [
  { value: "hidden", label: "Off" },
  { value: "avg2", label: "2w avg" },
  { value: "avg4", label: "4w avg" },
  { value: "avg8", label: "8w avg" },
] as const;

export type AverageOption = (typeof AVERAGE_OPTIONS)[number]["value"];

const AVERAGE_WEEKS: Record<Exclude<AverageOption, "hidden">, number> = { avg2: 2, avg4: 4, avg8: 8 };

// Room on the right for the two target labels, so they never sit behind a bar.
const TARGET_LABEL_GUTTER = 78;

// Trusted is a subset of everything concluded, so the two are one stack rather than
// two bars: brand Violet at the base, brand Olive for the excluded remainder above.
// Both brand tints are pale, so each segment carries a dark outline for edge
// definition against the surface. The trends are an annotation over that stack and
// wear neutral ink — near-black for trusted, mid-grey for all.
const chartConfig = {
  trusted: {
    label: "Trusted",
    color: "hsl(var(--chart-brand-violet))",
  },
  excluded: {
    label: "Not trusted",
    color: "hsl(var(--chart-brand-olive))",
  },
  total: {
    label: "Concluded",
    color: "transparent",
  },
  avg2: { label: "Trusted 2w trend", color: "hsl(var(--foreground))" },
  avg4: { label: "Trusted 4w trend", color: "hsl(var(--foreground))" },
  avg8: { label: "Trusted 8w trend", color: "hsl(var(--foreground))" },
  rawAvg2: { label: "Concluded 2w trend", color: "hsl(var(--muted-foreground))" },
  rawAvg4: { label: "Concluded 4w trend", color: "hsl(var(--muted-foreground))" },
  rawAvg8: { label: "Concluded 8w trend", color: "hsl(var(--muted-foreground))" },
} satisfies ChartConfig;

type ThroughputChartProps = {
  data: EvidenceGateWeeklyPoint[];
  targetPerWeek: number;
  targetRawPerWeek: number;
  average: AverageOption;
};

type TargetLabelProps = {
  series: string;
  muted?: boolean;
  // Injected by Recharts when it clones the label element.
  viewBox?: { x?: number; y?: number; width?: number };
};

// A plain string label cannot wrap, and "Trusted Target" on one line either clips or
// forces a wide gutter. This renders the two words as stacked tspans instead.
function TargetLabel({ series, muted, viewBox }: TargetLabelProps) {
  if (!viewBox) {
    return null;
  }
  const x = (viewBox.x ?? 0) + (viewBox.width ?? 0) + 8;
  return (
    <text
      y={viewBox.y ?? 0}
      fill={muted ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))"}
      fontSize={12}
      fontWeight={600}
    >
      <tspan x={x} dy="-0.1em">
        {series}
      </tspan>
      <tspan x={x} dy="1.15em">
        Target
      </tspan>
    </text>
  );
}

// ChartTooltipContent takes no children, so the stack total is appended as one more
// payload entry. That way it renders through the same markup as every other row
// instead of this chart hand-rolling the tooltip.
function ThroughputTooltip(props: React.ComponentProps<typeof ChartTooltipContent>) {
  // Wider than the 8rem default: "Concluded 4w trend" and its value are laid out with
  // justify-between, so at the default width a long label runs into the number.
  const className = cn("min-w-[14rem]", props.className);
  const payload = props.payload ?? [];
  if (!payload.length) {
    return <ChartTooltipContent {...props} className={className} />;
  }

  const totalRow = {
    name: "total",
    dataKey: "total",
    value: stackTotal(payload),
    color: "transparent",
    payload: {},
  };

  return <ChartTooltipContent {...props} className={className} payload={[...payload, totalRow]} />;
}

export function ThroughputChart({ data, targetPerWeek, targetRawPerWeek, average }: ThroughputChartProps) {
  const rows = buildChartRows(data);

  return (
    <ChartContainer config={chartConfig} className="min-h-[280px] w-full">
      <ComposedChart data={rows} margin={{ top: 8, right: TARGET_LABEL_GUTTER, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="week_start_utc" tickLine={false} axisLine={false} tickMargin={8} minTickGap={24} />
        <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
        <ChartTooltip content={<ThroughputTooltip />} />
        <Legend content={<ChartLegendContent />} />

        <Bar
          dataKey="trusted"
          stackId="concluded"
          fill="var(--color-trusted)"
          stroke="hsl(var(--chart-stack-outline))"
          strokeWidth={1}
        />
        <Bar
          dataKey="excluded"
          stackId="concluded"
          fill="var(--color-excluded)"
          stroke="hsl(var(--chart-stack-outline))"
          strokeWidth={1}
          radius={[4, 4, 0, 0]}
        />

        {average !== "hidden" && (
          <Line
            type="monotone"
            dataKey={rawAverageKey(AVERAGE_WEEKS[average])}
            stroke="hsl(var(--muted-foreground))"
            strokeWidth={2}
            dot={false}
            activeDot={false}
            connectNulls={false}
          />
        )}
        {average !== "hidden" && (
          <Line
            type="monotone"
            dataKey={average}
            stroke="hsl(var(--foreground))"
            strokeWidth={2}
            dot={false}
            activeDot={false}
            connectNulls={false}
          />
        )}

        {/* Drawn after the bars so the lines and their labels sit on top. */}
        {targetRawPerWeek > 0 && (
          <ReferenceLine
            y={targetRawPerWeek}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="4 4"
            label={<TargetLabel series="Concluded" muted />}
          />
        )}
        {targetPerWeek > 0 && (
          <ReferenceLine
            y={targetPerWeek}
            stroke="hsl(var(--foreground))"
            strokeDasharray="4 4"
            label={<TargetLabel series="Trusted" />}
          />
        )}
      </ComposedChart>
    </ChartContainer>
  );
}
